import {
  generateClassNames,
  generateMonth,
  getModifiers,
  generateStylesFromModifiers,
  validateDate
} from './atoms/helper';
import { EffectReducerExec, EffectsMap } from 'use-effect-reducer';
import dayjs from 'dayjs';

export interface DateRange {
  start?: Date;
  end?: Date;
}

export interface MonthsData {
  month: string;
  year: string;
  monthIndex: number;
  date: Date;
  id: `${string}-${string}`; // Unique identifier for selection
  sortKey: number; // For range comparison
}
export interface StringDateRange {
  start?: string;
  end?: string;
}

export type BlackoutDaysRange = {
  start: string;
  end: string;
};

export type BlackoutDay = string | BlackoutDaysRange;

export interface FlexibleDateRangeValue {
  name: string;
  value: string | number;
  key: string;
}

export interface FlexibleDateRangeValues {
  values: Array<FlexibleDateRangeValue>;
  defaultValue: number;
}
export type ModifierIndex =
  | 'today'
  | 'interactive'
  | 'hidden'
  | 'selected'
  | 'disabled'
  | 'past'
  | 'beginRange'
  | 'endRange'
  | 'beforemonth'
  | 'aftermonth'
  | 'preSelected'
  | 'blackout';

export type IModifiers = {
  [key in ModifierIndex]: boolean;
};

export type ModifiersColors = {
  [index: string]: any;
};

export interface ModifierCallable {
  (day: Date): boolean;
}

export type ModifierRange = {
  before: Date;
  after: Date;
};

export type DateModifier = {
  [key in ModifierIndex]: ModifierCallable | ModifierRange;
};

export type CommonDateProps = {
  /**
   * Show or hide the days of before and after month
   */
  showRemainingDays: boolean;
  /**
   * Allow setttings for calendar component
   */
  calendarProps: any;
  /**
   * Allowed char to enter in input
   */
  allowedCharacters: string;
  /**
   * Class when component recive focus
   */
  focusClass: string;
  /**
   * Setting of input icon when popover is shown
   */
  rightIconOpen: any;
  /**
   * Setting of input
   */
  rightIcon: any;
  /**
   * Show separator
   */
  showSeparator: boolean;
  /**
   * day style props
   */
  /**
   * Modifiers colors style
   */
  modifiersColors: ModifiersColors;
  /**
   * renderDay function
   */
  renderDay: Function | null;
  /**
   * renderDay function
   */
  renderWeekdayElement: Function | null;
  /**
   * Settings for weekdays
   */
  weekdayItem: any;
  /**
   * Color of navigation buttons
   */
  navColor: string;
  /**
   * Settings for navigation
   */
  navigationItem: NavigationItem;
  /**
   * renderNavBarElement
   */
  renderNavbarElement: Function | null;
  /**
   * Patter to format date
   */
  format: string;
  /**
   * determine if show the calendar in One or Two Columns
   */
  showInOneColumn: boolean;
  popover: any;
  /**
   * Date format for the screen readers
   */
  inputDateScreenReader: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'D MMMM, YYYY' | 'MMMM D, YYYY';
  availableDays: number;
  captionItem: any;
  numberOfMonths: number;
  /**
   * Class when the value is invalid
   */
  invalidClass: string;

  onValueClass: string;

  dayItems: any;
  enableFlexibleDates: boolean;
  checkedFlexibleDates: boolean;
  flexibleDatesRangeValues: FlexibleDateRangeValues;
  footerCheckboxComponent: Record<string, object>;
  footerContainerComponent: Record<string, object>;
  footerSelectComponent: Record<string, object>;
  footerInformationTextComponent: Record<string, object>;
  footerDoneButtonComponent: Record<string, object>;
  footerClearButtonComponent: Record<string, object>;

  blackoutDays: Array<BlackoutDay>;
  blackoutDaysFormat: string;
  errorLabelComponent?: any;
};

export type NavigationItem = any & {
  iconPrevProps: any;
  iconNextProps: any;
};

export type Focusable<T> = T & { focus: () => {} };
export const undefinedDate = { start: undefined, end: undefined };

export interface DayData {
  date: Date;
  modifiers: IModifiers;
  classes: string;
  styles: any;
}

//reducers types

export type CommonCalendarState = {
  selectedComponent: 'start' | 'end';
  activeTab: 'exact' | 'flexible';
  inputValue: StringDateRange;
  displayDate: Date;
  availableDays: number;
  months: Array<MonthHandler>;
  today: Date;
  currentDate: DateRange | null;
  selectedRange: DateRange | null;
  valid: boolean;
  validEnd: boolean;
  enableFlexibleDates: boolean;
  blackoutDays: Array<Required<DateRange>>;
  focusSelectedDay: boolean;
  isCheckedFlexibleDates: boolean;
  flexibleValue: string | number;
  preSelected: Date | undefined;
  previousDate: DateRange | null;
  popoverOpen: boolean;
  typeError?: number;
  typeErrorEnd?: number;
  currentMonth?: number;
  validateDone?: boolean;
  userInteraction?: boolean;
  tabActionOnInput?: boolean;
  currentMonthDateA11y?: Date;
  tripDuration?: {
    minimum: number;
    maximum: number;
  };
  monthsInterval?: {
    startMonth?: string | Date;
    endMonth?: string | Date;
  };
};

export type StateSingleDatePicker = CommonCalendarState & {};

export type StateSingleDateRangePicker = CommonCalendarState & {
  selectedRange: DateRange;
  machineStatus: string; //For use in state machine
};
type CommonActionsEffects =
  | { type: 'SET_PRESELECTED_DATE'; payload: Date | undefined; modifiers?: Partial<IModifiers> }
  | { type: 'SET_DISPLAY_DATE_ACTION'; payload: Date }
  | { type: 'SET_CURRENT_DATE'; payload: DateRange }
  | { type: 'SET_DATE_ACTION'; payload: Date | null; component: 'start' | 'end'; close: boolean }
  | { type: 'CLEAR_ACTION' }
  | { type: 'RESET_DATA_ACTION' }
  // | { type: 'ON_CLOSE_POPOVER_ACTION' }
  // | { type: 'ON_OPEN_POPOVER_ACTION' }
  | { type: 'CLOSE_POPOVER_ACTION' }
  | { type: 'CHANGE_ACTION' }
  | { type: 'CHANGE_CURRENT_MONTH'; payload: number }
  | { type: 'SET_CURRENT_MONTH_DATE_A11Y'; payload: Date | undefined }
  | { type: 'OPEN_POPOVER_ACTION' }
  | { type: 'CANCEL_ACTION' }
  | { type: 'RESET_ACTION' }
  | { type: 'RESET_ACTION_AND_CLEAR_STATE' }
  | { type: 'CLEAR_ACTION_DEPARTURE' }
  | { type: 'CLEAR_ACTION_RETURN' }
  | { type: 'UNDO_ACTION' }
  | { type: 'PARSE_INPUT_ACTION'; payload: string; component: 'start' | 'end' }
  | { type: 'SELECT_DAY_ACTION'; payload: DateRange }
  | { type: 'SET_TRIP_DURATION'; payload: any }
  | { type: 'SET_ACTIVE_TAB'; payload: 'exact' | 'flexible' }
  | {
      type: 'SELECT_MONTH_RANGE';
      payload: DateRange & { startMonth?: string | Date; endMonth?: string | Date };
    };

export type CommonCalendarActions =
  | CommonActionsEffects
  | { type: 'SET_PREVIOUS_DATE'; payload: DateRange | null }
  | { type: 'SET_FLEXIBLE_DATES_CHECKED'; payload: boolean }
  | { type: 'SET_USER_INTERACTION_ACTION'; payload: boolean }
  | { type: 'SET_TAB_ACTION_INPUT'; payload: boolean }
  | { type: 'SET_FLEXIBLE_DATES_VALUE'; payload: string | number }
  | { type: 'SET_FOCUS_SELECTED_DAY'; payload: boolean }
  | { type: 'SET_MONTHS'; payload: Array<MonthHandler> }
  | { type: 'SET_TYPE_ERROR'; payload: number }
  | { type: 'SET_TYPE_ERROR_END'; payload: number }
  | { type: 'SET_VALID'; payload: boolean }
  | { type: 'SET_VALID_END'; payload: boolean }
  | { type: 'SET_DAY_FOCUS_BY_KEYBOARD'; payload: boolean }
  | { type: 'SET_POPOVER'; payload: { popoverOpen: boolean; focusSelectedDay: boolean } }
  | { type: 'SET_INPUT_VALUE'; payload: string; component: 'start' | 'end' }
  | { type: 'SET_SELECTED_INPUT'; payload: 'start' | 'end' }
  | { type: 'VALIDATE_DONE' }
  | { type: 'MIN_TRIP_DURATION_DAYS'; payload: Array<Required<DateRange>> };

export type EventSingleDatePicker = CommonCalendarActions;
export type EventSingleDateRangePicker = CommonCalendarActions;

export type CommonCalendarEffects = CommonActionsEffects;

export type EffectSingleDatePicker = CommonCalendarEffects;

export type EffectSingleDateRangePicker = CommonCalendarEffects & {};
//TODO: Eliminate unused types
export type CalendarEffectsMap<
  S extends CommonCalendarState,
  A extends CommonCalendarActions,
  E extends CommonCalendarEffects
> = EffectsMap<S, A, E>;

export type CalendarReducerFn<
  S extends CommonCalendarState,
  A extends CommonCalendarActions,
  E extends CommonCalendarEffects
> = (state: S, action: A, exec: EffectReducerExec<S, A, E>) => S;

export class MonthHandler {
  private readonly month: Date;
  private modifiers: any;
  private readonly data: Array<Array<DayData>>;

  constructor(
    month: Date,
    modifiers: any,
    modifiersColors: any,
    blackOutDays: Array<Required<DateRange>>,
    showRemainingDays: boolean = false,
    locale: $TSFixMe = {}
  ) {
    this.month = month;

    this.modifiers = modifiers;
    this.data = generateMonth(
      month,
      showRemainingDays,
      (date) => {
        const dateModifiers = getModifiers(date, modifiers);
        const styles = generateStylesFromModifiers(dateModifiers, modifiersColors);
        const classes = generateClassNames(dateModifiers);
        return {
          date,
          classes,
          styles,
          modifiers: dateModifiers
        };
      },
      locale
    );
  }
  public getMonth() {
    return this.month;
  }

  public getMonthData() {
    return this.data;
  }

  public firstAvailableDay(): Date | undefined {
    let available;

    for (let i = 0; i < this.data.length; i++) {
      const week = this.data[i];
      const days = week.filter((day) => {
        return validateDate(day.date, day.modifiers);
      });
      if (days.length > 0 && days[0].date) {
        available = days[0].date;
        break;
      }
    }
    return available;
  }

  public findDay(date: Date): DayData | null {
    let data = null;
    this.data.every((week) => {
      let result = true;

      week.every((dayData) => {
        if (dayjs(dayData.date).isSame(dayjs(date), 'day')) {
          data = { ...dayData };
          result = false;
        }
        return result;
      });
      return result;
    });
    return data;
  }
}
